import {
  Component, EventEmitter, Input, OnDestroy, OnInit, Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { UpdateUserComponent } from 'src/app/routes/manage-staff/components/staff-list/components/update-user/update-user.component';
import { AuthService } from 'src/app/services/auth.service';
import { UsersService } from 'src/app/services/users.service';
import { EntityService } from 'src/app/services/entity.service';
import { ChangePasswordModalComponent } from '../change-password-modal/change-password-modal.component';
import { EditAvatarComponent } from '../edit-avatar/edit-avatar.component';
import { GeneralDataCell } from 'src/app/components/new-table/table-cell/table-cell.component';
import { NewTableData, TableRow } from 'src/app/components/new-table/new-table.component';
import { Router } from '@angular/router';

// TODO: this is a poorly named component. It should be renamed to something like ProfileOverviewComponent
// TODO: this component will need a ui update and may break once the requirement groups are implemented in the backend
// TODO: update user image and implement conditional allowance of editing the image based on permissions
@Component({
  selector: 'app-my-profile',
  templateUrl: './my-profile.component.html',
  styleUrls: [ './my-profile.component.scss' ],
})
export class MyProfileComponent implements OnInit, OnDestroy {
  @Input() user: any;
  @Input() viewingOthersProfile: any;
  @Output() refreshUser = new EventEmitter<any>();

  certificationList: { name: string; type: string; expirationDate: string }[] = [];
  authSubscription: Subscription;
  loggedIn$: any = false;
  loggedInSubscription: Subscription;
  user$: Observable<any>;
  entity$: Observable<any>;
  permissions: any;
  logoSrc!: string;
  searchTerm = '';

  // #region Managed Users Table
  managedUsers: TableRow<GeneralDataCell>[] = [];

  managedUsersTable: NewTableData<GeneralDataCell> = {
    title: 'Managed Users',
    rowStyling: 'default',
    noDataFoundMessage: 'No managed users found',
    selectable: false,
    hasBurgerMenu: false,
    displayNoDataMessage: true,
    columnTitles: [
      {
        name: 'Name',
        sortable: true,
        sortByProp: 'fullName',
        sortDirection: 'ASC',
        isDefaultSort: true,
      },
      {
        name: 'Assigned As',
        sortable: true,
        sortByProp: 'assignedAs',
      },
    ],
    data: [],
    meta: {
      usePagination: false,
      totalItems: 0,
      itemsPerPage: 0,
      curPage: 0,
      totalPages: 0,
      links: {
        prev: '',
        next: '',
        first: '',
        last: '',
        self: '',
      },
    },
  };

  // #endregion Managed Users Table

  constructor(
    public dialog: MatDialog,
    private usersService: UsersService,
    private authService: AuthService,
    private entityService: EntityService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.loggedInSubscription = this.authService.loggedIn$
      .subscribe((loggedInStatus: any) => {
        this.loggedIn$ = loggedInStatus
        if (loggedInStatus) {
          this.loadEntityLogo();
        }
      })
    this.user$ = this.authService.user$
    this.entity$ = this.authService.entity$
    this.user$.subscribe(user => {
    });
    this.authSubscription = this.authService.permissions$.subscribe((permissions: any) => {
      this.permissions = permissions;
    });

    this.formatManagedUsers();
  }

  loadEntityLogo(): void {
    this.authService.user$.subscribe(user => {
      if (user && user.loginName) {
        this.entityService.getEntityLogo(user.loginName).subscribe({
          next: (response: any) => {
            if (response && response.data && response.data.logoUrl) {
              this.logoSrc = response.data.logoUrl;
            }
          },
          error: (error) => {
            console.error('Failed to load entity logo:', error);
          },
        });
      }
    }).unsubscribe();
  }

  openChangePasswordModal = () => {
    const dialogRef = this.dialog.open(ChangePasswordModalComponent)

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.refreshUser.emit(result);
      }
    });
  }

  openUpdateModal = () => {
    const dialogRef = this.dialog.open(UpdateUserComponent, { data: this.user })
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.refreshUser.emit(result);
        this.usersService.getUserById(this.user.id, { 'includedAssociations': 'PrimaryBuilding, PrimaryPosition, Requirements, Roles, PrimaryApprovers, SecondaryApprovers, ManagedPrimaryUsers, ManagedSecondaryUsers' })
      }
    });
  }

  openChangeAvatarModal = () => {
    const dialogRef = this.dialog.open(EditAvatarComponent)

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.refreshUser.emit(result);
      }
    });
  }

  // #region Managed Users
  formatManagedUsers = () => {
    const primaryUsers = this.user?.ManagedPrimaryUsers;
    const secondaryUsers = this.user?.ManagedSecondaryUsers;

    const primaryUsersFormatted: TableRow<GeneralDataCell>[] = primaryUsers.map((user: any) => {
      const userRow: [GeneralDataCell, GeneralDataCell] = [
        {
          // NOTE: this will need to be updated to the user summary cell once implemented
          type: 'general',
          mainText: user.fullName,
          subText: user.contactEmail,
        },
        {
          type: 'general',
          mainText: 'Primary Manager',
          mainFontStyle: 'normal',
        },
      ];

      const userRowData: TableRow<GeneralDataCell> = {
        itemId: user.id,
        rowTooltip: user.fullName,
        columnData: userRow,
      }

      return userRowData;
    });

    const secondaryUsersFormatted: TableRow<GeneralDataCell>[] = secondaryUsers.map((user: any) => {
      const userRow: [GeneralDataCell, GeneralDataCell] = [
        {
          type: 'general',
          mainText: user.fullName,
          subText: user.contactEmail,
        },
        {
          type: 'general',
          mainText: 'Secondary Manager',
          mainFontStyle: 'normal',
        },
      ];

      const userRowData: TableRow<GeneralDataCell> = {
        itemId: user.id,
        rowTooltip: user.fullName,
        columnData: userRow,
      }

      return userRowData;
    });

    const allManagedUsers = [ ...primaryUsersFormatted, ...secondaryUsersFormatted ].sort((a, b) => a.rowTooltip.localeCompare(b.rowTooltip));

    this.managedUsersTable.data = allManagedUsers;
    this.managedUsers = allManagedUsers;
  }

  openUserProfile = (userId: number) => {
    this.router.navigate([ '/profile', userId ]);
  }

  sortManagedUsers = ({ columnName, sortDirection }: { columnName: string; sortDirection: 'ASC' | 'DESC' }) => {
    let sortedData = this.managedUsersTable.data;
    if (columnName === 'fullName') {
      sortedData = sortedData.sort((a, b) => {
        const aValue = a.rowTooltip.toString();
        const bValue = b.rowTooltip.toString();

        if (sortDirection === 'ASC') {
          return aValue.localeCompare(bValue);
        }
        return bValue.localeCompare(aValue);
      });
    } else if (columnName === 'assignedAs') {
      sortedData = sortedData.sort((a, b) => {
        const aValue = a.columnData[1].mainText.toString();
        const bValue = b.columnData[1].mainText.toString();

        if (sortDirection === 'ASC') {
          return aValue.localeCompare(bValue);
        }
        return bValue.localeCompare(aValue);
      });
    }
    this.managedUsersTable.data = sortedData;
  }

  searchManagedUsers = (searchTerm: string) => {
    const filteredUsers = this.managedUsers.filter((user) => {
      const name = user.rowTooltip.toLowerCase();
      const email = user.columnData[0].subText?.toString().toLowerCase();
      const type = user.columnData[1].mainText.toString().toLowerCase();

      return name.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase()) || type.includes(searchTerm.toLowerCase());
    });
    this.managedUsersTable.data = filteredUsers;
  }

  // #endregion Managed Users


  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
  }

}
